import {
  getDataTable,
  getDataTableByCanonicalType,
  TpChartConfigParams,
  TpChartDataBinding,
} from '../charts/chartDataBindings';

import { getColumnInfo } from '../../../data/analyticsDataSlice';

import styles from './Widget.module.scss';
import { TpDataContext, TpDataTable, UNBOUND_COLUMN, VALTYPE_ANY, VALTYPES } from '../../../data/interfaces';

import { discreteColorSets, numericalColorSets } from '../helpers/colors';
import { TpChartManifest, TpChartManifestAspectDef, TpChartManifestConfigParamDef } from '../charts/interfaces';
import { TpChartDefinition } from '../dashboard/interfaces';
import Switch from '../../../components/switch/Switch';
import { CONFIGPARAMTYPES } from '../helpers/configParams';
import { NumericalEditWithConformation } from '../../../components/numerical-edit-with-confirmation/NumericalEditWithConformation';
import { StringEditWithConfirmation } from '../../../components/string-edit-with-confirmation/StringEditWithConfirmation';
import { selectionFilter2Element } from '../../../data/selection/selectionUtils';
import { getUserError } from '../../../components/miscUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { checkConfigParamConditions } from '../helpers/configParamConditions';

export function isCompatibleDataType(aspectDataType: string | string[], columnValueType: string) {
  if (aspectDataType == VALTYPE_ANY) return true;
  if (Array.isArray(aspectDataType)) {
    for (const elem of aspectDataType) if (isCompatibleDataType(elem, columnValueType)) return true;
    return false;
  }
  if (aspectDataType == columnValueType) return true;
  // @todo: implement more sophisticated comparisons, e.g. categorical is compatible with string...
  return false;
}

function createColumnSelectList(lookupDataTable: TpDataTable, dataType: string) {
  let items: any = [];

  const compatibleColumns = lookupDataTable.columns.filter((column) =>
    isCompatibleDataType(dataType, column.valueType),
  );

  if (lookupDataTable.columnGroups.length == 0) {
    // we don't have groups => one flat list
    items = items.concat(
      compatibleColumns.map((column, columnIdx) => (
        <option key={column.id} value={column.id}>
          {column.name}
        </option>
      )),
    );
  } else {
    items = items.concat(
      compatibleColumns
        .filter((col) => !col.groupId)
        .map((column, columnIdx) => (
          <option key={column.id} value={column.id}>
            {column.name}
          </option>
        )),
    );
    items = items.concat(
      lookupDataTable.columnGroups
        .filter((group) => compatibleColumns.some((column) => column.groupId == group.id))
        .map((group) => (
          <optgroup key={group.id} label={group.name}>
            {compatibleColumns
              .filter((col) => col.groupId == group.id)
              .map((column) => (
                <option key={column.id} value={column.id}>
                  {column.name}
                </option>
              ))}
          </optgroup>
        )),
    );
  }

  return items;
}

function createBindingSelector(
  dataContext: TpDataContext,
  definition: TpChartDefinition,
  aspect: TpChartManifestAspectDef,
  currentDataBinding: TpChartDataBinding,
  onSecondaryTable: boolean,
  updateCustomBinding: any,
) {
  let lookupDataTable = getDataTable(dataContext, definition.dataBinding.dataTableId);
  let aspectBindings = currentDataBinding.aspectBindings;

  if (aspect.onColumnItems) {
    // datamatrix visualisation, and we want to look in table that is linked to the column
    if (!currentDataBinding.columnsCanonicalTypeId)
      throw getUserError(`Aspect is defined for column linked table but no column linked table was provided`);
    lookupDataTable = getDataTableByCanonicalType(dataContext, currentDataBinding.columnsCanonicalTypeId!);
  }

  if (onSecondaryTable) {
    lookupDataTable = getDataTable(dataContext, definition.dataBinding.secondDataTableId!);
    aspectBindings = currentDataBinding.secondAspectBindings!;
  }

  let items = [];
  if (!aspect.required)
    items.push(
      <option key={UNBOUND_COLUMN} value={UNBOUND_COLUMN}>
        {aspect.customAbsentLabel || 'None'}
      </option>,
    );

  if (isCompatibleDataType(aspect.dataType, VALTYPES.DENDROGRAM)) {
    for (const dendrogram of dataContext.dendrograms)
      if (dendrogram.canonicalTypeId == lookupDataTable.canonicalTypeId)
        items.push(
          <option key={`_dendrogram_${dendrogram.id}`} value={`_dendrogram_${dendrogram.id}`}>
            {dendrogram.name}
          </option>,
        );
  }

  let currentBoundColumn = UNBOUND_COLUMN;
  if (aspectBindings[aspect.id]) currentBoundColumn = aspectBindings[aspect.id].columnId || currentBoundColumn;

  items = items.concat(createColumnSelectList(lookupDataTable, aspect.dataType));

  return (
    <select
      value={currentBoundColumn}
      onChange={(ev) => {
        updateCustomBinding(aspect.id, ev.target.value, onSecondaryTable);
      }}
    >
      {items}
    </select>
  );
}

function createCompoundBindingSelector(
  dataContext: TpDataContext,
  definition: TpChartDefinition,
  aspect: TpChartManifestAspectDef,
  currentDataBinding: TpChartDataBinding,
  updateCustomCompoundBinding: any,
) {
  const lookupDataTable = getDataTable(dataContext, definition.dataBinding.dataTableId);
  const aspectBindings = currentDataBinding.compoundAspectBindings![aspect.id];

  const placeHolderAdd = '__add__';

  const items = [
    <option key={placeHolderAdd} value={placeHolderAdd}>
      {'Select a property to add'}
    </option>,
    ...createColumnSelectList(lookupDataTable, aspect.dataType),
  ];

  return (
    <div>
      {aspectBindings.map((colId) => (
        <div key={colId} className={styles.menuCompundAspectItem}>
          {getColumnInfo(lookupDataTable, colId).name}
          <button
            onClick={() => {
              updateCustomCompoundBinding(
                aspect.id,
                aspectBindings.filter((iterColId) => iterColId !== colId),
              );
            }}
          >
            <FontAwesomeIcon icon="xmark" />
          </button>
        </div>
      ))}
      <div className={styles.menuCompundAspectItem}>
        <select
          value={placeHolderAdd}
          onChange={(ev) => {
            const colId = ev.target.value;
            if (colId != placeHolderAdd) updateCustomCompoundBinding(aspect.id, [...aspectBindings, colId]);
          }}
        >
          {items}
        </select>
      </div>
    </div>
  );
}

function createConfigParamSelector(
  configParamDef: TpChartManifestConfigParamDef,
  currentConfigParams: TpChartConfigParams,
  updateConfigParam: any,
) {
  const paramType = configParamDef.paramType;
  if (paramType.configParamTypeId == CONFIGPARAMTYPES.MultipleChoice)
    return (
      <select
        value={currentConfigParams[configParamDef.id] ?? paramType.defaultValue}
        onChange={(ev) => {
          updateConfigParam(configParamDef.id, ev.target.value);
        }}
      >
        {paramType.choices.map((choice: any) => (
          <option key={choice.id} value={choice.id}>
            {choice.name}
          </option>
        ))}
      </select>
    );

  if (paramType.configParamTypeId == CONFIGPARAMTYPES.Boolean)
    return (
      <>
        <div style={{ display: 'inline-block', width: '60px', verticalAlign: 'top' }}>
          <Switch
            isOn={currentConfigParams[configParamDef.id]}
            onChanged={(newState: boolean) => {
              updateConfigParam(configParamDef.id, newState);
            }}
          />
        </div>
        <div style={{ display: 'inline-block', width: 'calc(100% - 60px)', verticalAlign: 'top', paddingTop: '2px' }}>
          {configParamDef.name}
        </div>
      </>
    );

  if (paramType.configParamTypeId == CONFIGPARAMTYPES.DiscreteColorScheme)
    return (
      <select
        value={currentConfigParams[configParamDef.id]}
        onChange={(ev) => {
          updateConfigParam(configParamDef.id, ev.target.value);
        }}
      >
        {discreteColorSets.map((colorSet) => (
          <option key={colorSet.name} value={colorSet.name}>
            {colorSet.name}
          </option>
        ))}
      </select>
    );

  if (paramType.configParamTypeId == CONFIGPARAMTYPES.NumericalColorScheme)
    return (
      <select
        value={currentConfigParams[configParamDef.id]}
        onChange={(ev) => {
          updateConfigParam(configParamDef.id, ev.target.value);
        }}
      >
        {numericalColorSets.map((colorSet) => (
          <option key={colorSet.name} value={colorSet.name}>
            {colorSet.name}
          </option>
        ))}
      </select>
    );

  if (paramType.configParamTypeId == CONFIGPARAMTYPES.Range)
    return (
      <input
        type="range"
        id={configParamDef.id}
        min={paramType.minVal}
        max={paramType.maxVal}
        step={paramType.step}
        value={currentConfigParams[configParamDef.id]}
        onChange={(ev) => {
          updateConfigParam(configParamDef.id, ev.target.value);
        }}
      />
    );

  if (paramType.configParamTypeId == CONFIGPARAMTYPES.Value)
    return (
      <NumericalEditWithConformation
        value={currentConfigParams[configParamDef.id]}
        onModified={(newValue: number) => {
          updateConfigParam(configParamDef.id, newValue);
        }}
      />
    );

  if (paramType.configParamTypeId == CONFIGPARAMTYPES.String)
    return (
      <StringEditWithConfirmation
        value={currentConfigParams[configParamDef.id]}
        onModified={(newValue: string) => {
          updateConfigParam(configParamDef.id, newValue);
        }}
      />
    );

  throw getUserError(`Invalid chart configuration parameter type: ${paramType.configParamTypeId}`);
}

interface TpWidgetMenuProps {
  chartTypeManifest: TpChartManifest;
  dataContext: TpDataContext;
  definition: TpChartDefinition;
  currentDataBinding: TpChartDataBinding;
  updateCustomBinding: (aspectId: string, columnId: string, onSecondaryTable: boolean) => void;
  updateCustomCompoundBinding: (aspectId: string, columnIds: string[]) => void;
  currentConfigParams: TpChartConfigParams;
  updateConfigParam: (configParamId: string, value: string) => void;
}

/**
 * Evaluates function-based configuration parameter types and updates
 * the current configParamDefs with the evaluated paramTypes and the configParams with the evaluated default values.
 * @param chartTypeManifest The chart type manifest containing configuration parameter definitions.
 * @param currentConfigParams The current configuration parameters.
 * @param dataContext The data context.
 * @param currentDataBinding The current data binding.
 * @returns The updated configParamDefs and configParams with evaluated values.
 */
function evaluateFunctionBasedConfigParamTypes(
  chartTypeManifest: TpChartManifest,
  currentConfigParams: TpChartConfigParams,
  dataContext: TpDataContext,
  currentDataBinding: TpChartDataBinding,
) {
  const updatedConfigParams = { ...currentConfigParams };
  const updatedConfigParamDefs = chartTypeManifest.configParams.map((configParamDef) => {
    if (typeof configParamDef.paramType === 'function') {
      const evaluatedParamType = configParamDef.paramType(updatedConfigParams, dataContext, currentDataBinding);
      if (updatedConfigParams[configParamDef.id] === undefined) {
        updatedConfigParams[configParamDef.id] = evaluatedParamType.defaultValue;
      }
      return { ...configParamDef, paramType: evaluatedParamType };
    }
    return configParamDef;
  });

  return { updatedConfigParamDefs, updatedConfigParams };
}

function WidgetMenu({
  chartTypeManifest,
  dataContext,
  definition,
  currentDataBinding,
  updateCustomBinding,
  updateCustomCompoundBinding,
  currentConfigParams,
  updateConfigParam,
}: TpWidgetMenuProps) {
  const dataTable = getDataTable(dataContext, definition.dataBinding.dataTableId);

  // Evaluate function-based configParamTypes and update currentConfigParams with evaluated default values
  // This ensures that the menu reflects the correct state based on the current data context and binding
  // And these values are up to date for checking visibility conditions of other configParams
  const { updatedConfigParamDefs, updatedConfigParams } = evaluateFunctionBasedConfigParamTypes(
    chartTypeManifest,
    currentConfigParams,
    dataContext,
    currentDataBinding,
  );

  // sanity check: do all aspects & configs belong to a group? if not => we miss some in the menu
  const groupMap: { [id: string]: boolean } = {};
  for (const group of chartTypeManifest.groups) groupMap[group.groupId] = true;
  for (const aspect of chartTypeManifest.aspects)
    if (!groupMap[aspect.groupId]) throw getUserError(`Invalid group id for aspect ${aspect.id}: ${aspect.groupId}`);
  for (const config of updatedConfigParamDefs)
    if (!config.hidden && !groupMap[config.groupId])
      throw getUserError(`Invalid group id for config ${config.id}: ${config.groupId}`);

  const groups = chartTypeManifest.groups.map((group) => {
    const aspectBindings = chartTypeManifest.aspects
      .filter((aspect) => aspect.groupId == group.groupId && !aspect.hidden)
      .map((aspect) => (
        <div key={aspect.id} className={styles.menuLine}>
          <div className={styles.menuLabel}>{aspect.name}</div>
          {createBindingSelector(dataContext, definition, aspect, currentDataBinding, false, updateCustomBinding)}
        </div>
      ));

    const secondaryAspectBindings =
      chartTypeManifest.secondTable &&
      chartTypeManifest.secondTable.aspects
        .filter((aspect) => aspect.groupId == group.groupId && !aspect.hidden)
        .map((aspect) => (
          <div key={aspect.id} className={styles.menuLine}>
            <div className={styles.menuLabel}>{aspect.name}</div>
            {createBindingSelector(dataContext, definition, aspect, currentDataBinding, true, updateCustomBinding)}
          </div>
        ));

    const compoundAspectBindings =
      chartTypeManifest.compoundAspects &&
      chartTypeManifest.compoundAspects
        .filter((aspect) => aspect.groupId == group.groupId && !aspect.hidden)
        .map((aspect) => (
          <div key={aspect.id} className={styles.menuLine}>
            <div className={styles.menuLabel}>{aspect.name}</div>
            {createCompoundBindingSelector(
              dataContext,
              definition,
              aspect,
              currentDataBinding,
              updateCustomCompoundBinding,
            )}
          </div>
        ));

    const configParams = updatedConfigParamDefs
      .filter((configParamDef) => configParamDef.groupId == group.groupId)
      .filter((configParamDef) =>
        checkConfigParamConditions(dataTable, configParamDef, currentDataBinding, updatedConfigParams),
      )
      .map((configParamDef) => (
        <div key={configParamDef.id} className={styles.menuLine}>
          {configParamDef.paramType.configParamTypeId != CONFIGPARAMTYPES.Boolean && (
            <div className={styles.menuLabel}>{configParamDef.name}</div>
          )}
          {createConfigParamSelector(configParamDef, updatedConfigParams, updateConfigParam)}
        </div>
      ));

    // skip empty groups
    if (
      aspectBindings.length == 0 &&
      !secondaryAspectBindings?.length &&
      !compoundAspectBindings?.length &&
      configParams.length == 0
    ) {
      return null;
    }

    return (
      <div key={group.groupId} className={styles.menuGroup}>
        <div className={styles.menuGroupName}>{group.groupName}</div>
        {aspectBindings}
        {secondaryAspectBindings}
        {compoundAspectBindings}
        {configParams}
      </div>
    );
  });

  return (
    <div>
      {groups}
      {currentDataBinding.filter && (
        <>
          <div className={styles.menuGroupName}>Filter</div>
          <div className={styles.menuLine}>
            {selectionFilter2Element(dataContext, dataTable.canonicalTypeId, currentDataBinding.filter)}
          </div>
        </>
      )}
    </div>
  );
}

export default WidgetMenu;
